/**
 * Internationalization Helpers
 *
 * Pure utility functions for locale resolution, URL parsing, and
 * localized path generation. These functions have no side effects
 * and can be used in any context (components, middleware, tests).
 */

import type { CountryConfig } from '@template-project/storefront-shared';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, isSupportedLocale } from './intl.config';
import type { SupportedLocale } from './intl.config';
import { COUNTRY_CONFIG } from './country-config';

/**
 * Get the country configuration for a given locale.
 * Returns the default locale config if the provided locale is not supported.
 */
export function getCountryConfig(locale: string): CountryConfig {
  if (isSupportedLocale(locale)) {
    return COUNTRY_CONFIG[locale];
  }
  return COUNTRY_CONFIG[DEFAULT_LOCALE];
}

/**
 * Get the URL prefix for a locale.
 * Returns empty string for default locale, or '/{locale}' for others.
 */
export function getLocalePrefix(locale: string): string {
  if (isSupportedLocale(locale)) {
    return COUNTRY_CONFIG[locale].localePrefix;
  }
  return '';
}

/**
 * Get all supported locales as a string array.
 */
export function getSupportedLocales(): readonly string[] {
  return SUPPORTED_LOCALES;
}

/**
 * Extract a supported locale from a URL pathname.
 *
 * Handles patterns like:
 * - '/en-CA/products/123' -> 'en-CA'
 * - '/en-GB/' -> 'en-GB'
 * - '/products/123' -> null (default locale, no prefix)
 * - '/en-XX/products' -> null (unsupported locale)
 */
export function getLocaleFromPath(pathname: string): SupportedLocale | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  const firstSegment = segments[0];
  if (isSupportedLocale(firstSegment)) {
    return firstSegment;
  }

  return null;
}

/**
 * Resolve the effective locale from a URL pathname.
 * Returns the locale found in the path, or DEFAULT_LOCALE if none.
 */
export function resolveLocaleFromPath(pathname: string): SupportedLocale {
  return getLocaleFromPath(pathname) ?? DEFAULT_LOCALE;
}

/**
 * Generate a localized path by prepending the appropriate locale prefix.
 *
 * Examples:
 * - formatLocalizedPath('/products', 'en-US') -> '/products'     (default, no prefix)
 * - formatLocalizedPath('/products', 'en-CA') -> '/en-CA/products'
 * - formatLocalizedPath('/', 'en-GB')         -> '/en-GB'
 */
export function formatLocalizedPath(path: string, locale: string): string {
  const prefix = getLocalePrefix(locale);

  if (!prefix) {
    return path;
  }

  // Avoid double slashes: '/en-CA' + '/' -> '/en-CA', not '/en-CA/'
  if (path === '/') {
    return prefix;
  }

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${prefix}${normalizedPath}`;
}

/**
 * Strip the locale prefix from a pathname, returning the base path.
 *
 * Examples:
 * - stripLocaleFromPath('/en-CA/products') -> '/products'
 * - stripLocaleFromPath('/en-GB')          -> '/'
 * - stripLocaleFromPath('/products')       -> '/products'  (no locale prefix)
 */
export function stripLocaleFromPath(pathname: string): string {
  const localeInPath = getLocaleFromPath(pathname);

  if (!localeInPath || localeInPath === DEFAULT_LOCALE) {
    return pathname;
  }

  const prefix = `/${localeInPath}`;
  const stripped = pathname.slice(prefix.length);
  return stripped || '/';
}
